import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import logo from "@/assets/catworth-logo.png";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/executive");
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#0F172A" }}>
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden flex-col justify-between p-12">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1738162837451-2041c1418f54?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(124,58,237,0.45))" }} />
        <div className="relative z-10">
          <div className="bg-white rounded-lg px-4 py-3 inline-flex">
            <img src={logo} alt="Catworth Laser" className="h-11 object-contain" />
          </div>
        </div>
        <div className="relative z-10">
          <h2 className="font-head text-4xl font-bold text-white leading-tight">
            Laser Operations<br />Intelligence Platform
          </h2>
          <p className="text-slate-300 mt-4 max-w-md text-sm leading-relaxed">
            The permanent record of laser nesting activity and processed-weight analytics — replacing the LASER WEIGHT 2026 workbook with a persistent, auditable command platform.
          </p>
          <div className="flex gap-6 mt-8">
            {[["789", "Nesting records"], ["3", "Machines"], ["36", "Customers"]].map(([n, l]) => (
              <div key={l}>
                <p className="font-mono text-2xl font-bold" style={{ color: "#A78BFA" }}>{n}</p>
                <p className="text-xs text-slate-400">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden bg-white rounded-lg px-4 py-3 inline-flex mb-8">
            <img src={logo} alt="Catworth Laser" className="h-10 object-contain" />
          </div>
          <h1 className="font-head text-2xl font-bold text-white">Sign in</h1>
          <p className="text-slate-400 text-sm mt-1 mb-7">Access the operations intelligence console</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block">Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
                placeholder="grant@granik.co.za" data-testid="login-email"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-11" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block">Password</Label>
              <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required
                placeholder="••••••••" data-testid="login-password"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-11" />
            </div>
            {err && <p className="text-sm text-red-400" data-testid="login-error">{err}</p>}
            <Button type="submit" disabled={busy} data-testid="login-submit"
              className="w-full h-11 text-white font-semibold" style={{ background: "#7C3AED" }}>
              {busy ? <Loader2 className="animate-spin" size={18} /> : <>Sign in <ArrowRight size={17} className="ml-1" /></>}
            </Button>
          </form>

        </div>
      </div>
    </div>
  );
}
